import React, { useState, useEffect } from 'react'
import { View } from '@instructure/ui-view'
import { Flex } from "@instructure/ui-flex"
import { Spinner } from "@instructure/ui-spinner"
import { connect } from 'react-redux'
import { TargetContainer } from '../dragAndDrop/TargetContainer'
import { getSections, deleteEnrollment, getEnrollment } from '../../actions/courses'
import { setSectionEnrollment, getSectionEnrollment, setEnrollmentAlert } from '../../actions/sections'
import ManageEnrolments from "../manageEnrolments/ManageEnrolments.jsx"
import { getHighestEnrollmentRoleId, uniqueArr } from '../../utils/commonFunctions'
import { ErrorBillBoard } from '@oxctl/ui-lti'
import { Text } from "@instructure/ui-text"
import { List } from '@instructure/ui-list'
import { Alert } from '@instructure/ui-alerts'
import { ToggleGroup } from '@instructure/ui-toggle-details'
import _ from 'lodash'

const ManageSections = ({
  token,
  sections,
  sectionCreated,
  courseId,
  enrollments,
  successfullyEnrolled,
  allEnrollments,
  setSectionEnrollment,
  deleteEnrollment,
  getSectionEnrollment,
  setEnrollmentAlert,
  getSections
}) => {
  const [registeredEnrollments, setRegisteredEnrollments] = useState([])
  const [loadedSections, setLoadedSections] = useState([])
  const [uiError, setUiError] = useState(false)
  const [errorMessageInfo, setErrorMessageInfo] = useState("")
  const [singleEnrollmentIds, setSingleEnrollmentIds] = useState([])
  const [_activeSectionId, setActiveSectionId] = useState(null)

  useEffect(() => {
    const handleDrop = () => {
      getEnrollment(courseId, token)
    }
    window.addEventListener("drop", handleDrop)
    return () => window.removeEventListener("drop", handleDrop)
  }, [courseId, token])

  useEffect(() => {
    if (sectionCreated?.result) {
      getSections(courseId, token)
    }
  }, [sectionCreated?.result, courseId, token])

  useEffect(() => {
    if (allEnrollments.length) {
      const blockedEnrollmentIds = []
      const enrollementGroupedByUser = _.chain(allEnrollments).groupBy("user_id").value()
      _.forEach(enrollementGroupedByUser, (value, key) => {
        if (value.length === 1) {
          blockedEnrollmentIds.push(key.toString())
        }
      })
      setSingleEnrollmentIds(blockedEnrollmentIds)
    }
  }, [allEnrollments])

  useEffect(() => {
    const enrollmentResult = enrollments?.result?.data
    const enrollmentSectionId = enrollments?.result?.id
    if (enrollmentResult) {
      const filteredEnrollments = registeredEnrollments.filter(enr =>
        enr.course_section_id !== enrollmentSectionId
      )
      setRegisteredEnrollments(uniqueArr([...filteredEnrollments, ...enrollmentResult]))
    }
  }, [enrollments?.result?.data, enrollments?.result?.id])

  useEffect(() => {
    const sectionResult = sections?.result?.data
    if (sectionResult?.length) {
      setLoadedSections(sectionResult)
    }
  }, [sections?.result?.data])

  const handleDrop = (draggedEventObj, value, enrolledId = false) => {
    setActiveSectionId(value.id)

    // Check if user is already enrolled in this section
    const userSectionIds = draggedEventObj?.enrollments?.map(e => e.course_section_id)
    if (userSectionIds?.includes(value.id)) {
      setUiError(true)
      setErrorMessageInfo("This user has already been added to this section")
      return
    }

    getSectionEnrollment(value.id, token)

    if (!registeredEnrollments.length && enrollments?.working) {
      return
    }

    if (draggedEventObj && value) {
      const alreadyEnrolled = registeredEnrollments.filter(enr =>
        enr.course_section_id === value.id && enr.user_id === draggedEventObj.id
      )

      if (alreadyEnrolled?.length) {
        setUiError(true)
        setErrorMessageInfo("This user has already been added to this section")
      } else {
        setActiveSectionId(value.id)

        if (enrolledId) {
          deleteEnrollment(enrolledId, courseId, token)
        }

        const currentEnrollment = [...(draggedEventObj.enrollments || [])]
        const roleId = getHighestEnrollmentRoleId(currentEnrollment)

        if (roleId && draggedEventObj.id && token) {
          const sectionData = { ...value }
          setSectionEnrollment(sectionData, draggedEventObj.id, roleId, token)
          setEnrollmentAlert(true)
        }
      }
    }
  }

  const handleToggle = (sectionId) => {
    getSectionEnrollment(sectionId, token)
  }

  if (!loadedSections.length && sections?.working) {
    return <Spinner renderTitle="Loading"/>
  }

  if (sections?.error) {
    return <ErrorBillBoard message={sections.error.message} />
  }

  return (
    <View>
      {uiError && (
        <Alert
          variant="error"
          timeout={2000}
          renderCloseButtonLabel="Close"
          onDismiss={() => {
            setUiError(false)
            setErrorMessageInfo("")
          }}
        >
          {errorMessageInfo}
        </Alert>
      )}
      {successfullyEnrolled && (
        <Alert
          variant="success"
          timeout={5000}
          renderCloseButtonLabel="Close"
          onDismiss={() => setEnrollmentAlert(false)}
        >
          User successfully enrolled
        </Alert>
      )}
      <View display="block" height="75vh" overflowY="auto" tabIndex="0">
        <List margin="none" isUnstyled aria-labelledby='sections-header'>
          {loadedSections.map(sect => {
            const usersEnrolled = registeredEnrollments.filter(enr => enr.course_section_id === sect.id)
            const totalEnrolledUsers = usersEnrolled.length || sect.total_students
            return (
              <List.Item key={sect.id}>
                <TargetContainer onDrop={(e, enrolledId) => handleDrop(e, sect, enrolledId)} type="fromUsers">
                  <View margin="x-small x-small x-small none" padding="x-small" display="block" cursor="pointer">
                    <ToggleGroup
                      // If we don't disable the transition we don't render the spinner.
                      transition={false}
                      onToggle={(_, expanded) => expanded && handleToggle(sect.id)}
                      summary={
                        <Flex justifyItems="space-between">
                          <Flex.Item size="50%">
                            <Text weight="bold" size="small">{sect.name}</Text>
                          </Flex.Item>
                          <Flex.Item size="50%">
                            <Text size="small">{totalEnrolledUsers} users</Text>
                          </Flex.Item>
                        </Flex>
                      }
                      toggleLabel={(expanded) =>
                        expanded ? `Collapse ${sect.name} section` : `Expand ${sect.name} section`
                      }
                    >
                      <View as='div' padding='x-small'>
                        {enrollments.working ? (
                          <Spinner renderTitle='Loading section' />
                        ) : (
                          <ManageEnrolments
                            onDropEnrollment={(e) => handleDrop(e, sect)}
                            usersEnrolled={usersEnrolled}
                            singleEnrollmentIds={singleEnrollmentIds}
                          />
                        )}
                      </View>
                    </ToggleGroup>
                  </View>
                </TargetContainer>
              </List.Item>
            );
          })}
        </List>
      </View>
    </View>
  );
}

const mapStateToProps = (state) => {
  const {
    userAccess: { jwtToken },
    courses: { sectionCreated, sections, allEnrollments },
    sections: { enrollments, successfullyEnrolled },
    canvas: { canvasCourse }
  } = state

  return {
    token: jwtToken,
    sections,
    sectionCreated,
    courseId: canvasCourse && canvasCourse.courseId,
    enrollments,
    successfullyEnrolled,
    allEnrollments: (allEnrollments?.result?.data) || []
  }
}

export default connect(
  mapStateToProps,
  {
    getSections,
    setSectionEnrollment,
    deleteEnrollment,
    getEnrollment,
    getSectionEnrollment,
    setEnrollmentAlert
  }
)(ManageSections)